import {appURLS, appValues} from 'appRoot/app.constants';

/**
 * Description:
 * Uses service workers' Cache API to store app static files offline.
 * See README.md `## Versioning and offline` for info.
 */

const NS = 'SwCacheService'; // jshint ignore:line
const SW_SCRIPT = `/cache-all-requests.js?baseAPI=${appURLS.API}&server=${
  appValues.IS_LOCAL ? 'webpackDevServer' : 'nginx'
}`;
const CACHE_NAME = 'nbi-diary-cache'; // this is also hardcoded in `cache-all-requests.js`

class SwCacheService {
  private isRegistered = false;
  private availableVersion: string;

  doCache() {
    const supportsCache = 'serviceWorker' in navigator;
    if (!supportsCache) {
      console.log(NS, 'Service workers not supported on this browser. Offline mode not available.');
    }
    return !appValues.SKIP_CACHE && supportsCache;
  }

  setAvailableVersion(v: string): void {
    this.availableVersion = v;
  }

  getAvailableVersion(): string {
    return this.availableVersion;
  }

  /**
   * deletes the service worker that caches the app for offline mode
   * @param cb - callback with potential error message
   */
  deleteCache(cb: (err?: string) => void) {
    if (!this.doCache()) {
      cb();
      return;
    }
    window.caches?.delete(CACHE_NAME)
      .then(() => {
        cb();
      })
      .catch(error => {
        console.warn(NS, 'Could not delete cache', error);
        cb(error);
      });
  }


  /**
   * Will unregister running service worker when doCache is false.
   * Useful if you ran local version of Harbor docker container, which started the service worker,
   * and then you go back to webpack dev server afterwards.
   */
  unregisterWhenSkipped() {
    if (!this.doCache()) {
      navigator.serviceWorker?.getRegistration(SW_SCRIPT)
        .then(reg => reg.unregister())
        .then(success => {
          if(success) {
            console.log(NS, 'Service worker unregistered successfully');
          } else {
            console.warn(NS, 'Service worker failed to unregister');
          }
          this.isRegistered = false;
        })
        .catch(error => {
          console.warn(NS, 'Service worker caught an error and failed to unregister', error);
        });
    }
  }

  cacheUnregister(cb: (err?: string) => void): void {
    if (!this.doCache()) {
      cb();
      return;
    }
    navigator.serviceWorker.getRegistration(SW_SCRIPT)
      .then(reg => reg.unregister())
      .then(success => {
        if(success) {
          console.log(NS, 'Service worker unregistered successfully');
        } else {
          console.warn(NS, 'Service worker failed to unregister');
        }
        this.isRegistered = false;
        cb();
      })
      .catch(error => {
        console.warn(NS, 'Service worker caught an error and failed to unregister', error);
        cb(error);
      });
  }

  cacheRegister(): void {
    if (!this.doCache()) {
      return;
    }
    navigator.serviceWorker.register(SW_SCRIPT, { scope: '/' })
      .then(() => navigator.serviceWorker.ready)
      .then(reg => {
        console.log(NS, '-----reg', reg);

        if(reg.installing) {
          console.log(NS, 'Service worker installing');
        } else if(reg.waiting) {
          console.log(NS, 'Service worker installed');
        } else if(reg.active) {
          console.log(NS, 'Service worker active');
        }
        this.isRegistered = true;
      })
      .catch(error => {
        console.log(NS, 'error when registering service worker', error)
      });
  }

  /**
   * Checks cache for an image to know if the cache is up to date or if it has been cleared.
   * The image that is checked for never gets loaded by the app, just used for checking cache.
   */
  checkCache(): Promise<boolean> {
    const FILE_PATH_TO_CHECK = '/images/cache-checker.jpg';

    return window.caches?.match(FILE_PATH_TO_CHECK, {cacheName: CACHE_NAME})
      .then(results => {
        console.log('checkCache', results);
        return !!results;
      });
  }
}

const instance = new SwCacheService();
export default instance as SwCacheService
